import axios from 'axios';
import getAuthSession from './auth';
import { FetchAllWorkflowsRequest } from '../app/store/workflowSlice';
import { Workflow, WorkflowTypeKey } from '../interfaces/workflow.interface';
import { QuestionnaireVersions } from './questionnaires';

export const WORKFLOW_STATUSES = {
  INCOMPLETE: 'incomplete',
  NON_COMPLIANT: 'non-compliant',
  COMPLETE: 'complete',
  SUBMITTED_FOR_ASSESSMENT: 'submittedForAssessment',
  ASSIGNED: 'Assigned',
  PRE_ASSESSMENT: 'Pre-Assessment',
  PRE_ASSESSMENT_INCOMPLETE: 'Pre-Assessment Incomplete',
  ASSESSING: 'Assessing',
  SUBMITTED: 'Submitted',
  REJECTED: 'Rejected',
  REMEDIATION: 'Remediation',
  FINAL_COMPLETE: 'Complete',
} as const;

export const wfHierarchy = [
  WORKFLOW_STATUSES.INCOMPLETE,
  WORKFLOW_STATUSES.NON_COMPLIANT,
  WORKFLOW_STATUSES.COMPLETE,
  WORKFLOW_STATUSES.SUBMITTED_FOR_ASSESSMENT,
  WORKFLOW_STATUSES.ASSIGNED,
  WORKFLOW_STATUSES.PRE_ASSESSMENT,
  WORKFLOW_STATUSES.PRE_ASSESSMENT_INCOMPLETE,
  WORKFLOW_STATUSES.ASSESSING,
  WORKFLOW_STATUSES.SUBMITTED,
  WORKFLOW_STATUSES.REJECTED,
  WORKFLOW_STATUSES.REMEDIATION,
  WORKFLOW_STATUSES.FINAL_COMPLETE,
];

export type WorkflowStatus = typeof WORKFLOW_STATUSES[keyof typeof WORKFLOW_STATUSES];

export const PRE_ASSESSMENT_STATUSES: WorkflowStatus[] = [
  WORKFLOW_STATUSES.PRE_ASSESSMENT,
  WORKFLOW_STATUSES.PRE_ASSESSMENT_INCOMPLETE,
];

export type AssessmentWfStatusType =
  | typeof WORKFLOW_STATUSES.ASSESSING
  | typeof WORKFLOW_STATUSES.SUBMITTED
  | typeof WORKFLOW_STATUSES.REMEDIATION
  | typeof WORKFLOW_STATUSES.FINAL_COMPLETE;

export const assessmentWfStatuses: Set<AssessmentWfStatusType> = new Set([
  WORKFLOW_STATUSES.ASSESSING,
  WORKFLOW_STATUSES.SUBMITTED,
  WORKFLOW_STATUSES.REMEDIATION,
  WORKFLOW_STATUSES.FINAL_COMPLETE,
]);

export function isAssessmentWfStatus(val: string): boolean {
  return assessmentWfStatuses.has(val as AssessmentWfStatusType);
}

export async function createNewWorkflow(props: { workflowType: WorkflowTypeKey, baseline: number, serviceProvider: number, status: string, site?: number, application?: number, applicationVersion?: number, version?: number }) {
  const authSession = await getAuthSession();
  const response = await axios.post(
    `${process.env.REACT_APP_BASE_API}/workflows/`,
    { ...props, isActive: true },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function fetchWorkflowById(workflow_id: number) {
  const authSession = await getAuthSession();
  const response = await axios.get(
    `${process.env.REACT_APP_BASE_API}/workflows/${workflow_id}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as Workflow;
}

export async function fetchWorkflows(request: FetchAllWorkflowsRequest, isActive?: boolean) {
  const authSession = await getAuthSession();

  const queryString = Object.entries(request)
    .filter(([, val]) => val !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const activeParam =
    typeof isActive !== 'undefined' ? `&is_active=${isActive}` : '';

  const url = `${process.env.REACT_APP_BASE_API}/workflows/?limit=100${activeParam}${
    queryString ? `&${queryString}` : ''
  }`;

  const response = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
    },
  });

  return response.data.results as Workflow[];
}

export async function deactivateWorkflowProcess(workflow_id: number) {
  const authSession = await getAuthSession();
  const response = await axios.patch(
    `${process.env.REACT_APP_BASE_API}/workflows/deactivate/`,
    {
      workflow_id,
    },
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data;
}

export async function deactivateActiveWorkflows(activeWorkflowIds: number[]) {
  const responses = await Promise.all(activeWorkflowIds.map(async (id) => {
    return deactivateWorkflowProcess(id);
  }));
  return responses;
}

export const handleDeativateWorkflows = async (workflows: Workflow[]) => {
  if (!workflows || workflows.length === 0) return;

  const activeWorkflowIds: number[] = workflows.filter(wf => wf.isActive).map(wf => wf.id);
  await deactivateActiveWorkflows(activeWorkflowIds);
};

export async function updateWorkflow(request: Partial<Workflow>) {
  const authSession = await getAuthSession();
  const { id,  ...req } = request;
  const currentWorkflow = await axios.get(
    `${process.env.REACT_APP_BASE_API}/workflows/${id}/`,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  
  const currentStatus = currentWorkflow.data?.status;

  const currentStatusIndex = wfHierarchy.indexOf(currentStatus || '');
  const newStatusIndex = wfHierarchy.indexOf(req.status as typeof wfHierarchy[number] || '');
  const statusExceptions = (currentStatus === 'complete' && req.status === 'incomplete') || (currentStatus === 'non-compliant' && req.status === 'incomplete');
  // If the new status is lower in the hierarchy, just use the current status unless in pre-assessment phase
  if (newStatusIndex < currentStatusIndex && !statusExceptions) {
    req.status = currentStatus;
  }
  const url = `${process.env.REACT_APP_BASE_API}/workflows/${id}/`;
  const response = await axios.patch(
    url, req,
    {
      headers: { 'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}` },
    },
  );
  return response.data as Workflow;
}

// Constants for workflow types
export const TPN_BEST_PRACTICE_5_1 = 'tpn_best_practices_5.1';
export const TPN_BEST_PRACTICE_5_2 = 'tpn_best_practices_5.2';
export const TPN_BEST_PRACTICE_5_3 = 'tpn_best_practices_5.3';
export const TPN_BEST_PRACTICE_5_3_1 = 'tpn_best_practices_5.3.1';

interface WorkflowEntry {
  label: string;
  version: string;
  questionnaireTitle: string;
  questionnaireVersion: QuestionnaireVersions;
  sortOrder: number;
}

export type WorkflowMap = {
  [key in typeof TPN_BEST_PRACTICE_5_1 | typeof TPN_BEST_PRACTICE_5_2 | typeof TPN_BEST_PRACTICE_5_3 | typeof TPN_BEST_PRACTICE_5_3_1]: WorkflowEntry;
};

export const WORKFLOW_MAP: WorkflowMap = {
  [TPN_BEST_PRACTICE_5_1]: {
    label: 'Questionnaire v5.1',
    version: 'v5.1',
    questionnaireVersion: QuestionnaireVersions.V_5_1_0,
    questionnaireTitle: 'TPN Best Practices Questionnaire v5.1',
    sortOrder: 0,
  },
  [TPN_BEST_PRACTICE_5_2]: {
    label: 'Questionnaire v5.2',
    version: 'v5.2',
    questionnaireVersion: QuestionnaireVersions.V_5_2_0,
    questionnaireTitle: 'TPN Best Practices Questionnaire v5.2',
    sortOrder: 1,
  },
  [TPN_BEST_PRACTICE_5_3]: {
    label: 'Questionnaire v5.3',
    version: 'v5.3',
    questionnaireVersion: QuestionnaireVersions.V_5_3_0,
    questionnaireTitle: 'TPN Best Practices Questionnaire v5.3',
    sortOrder: 2,
  },
  [TPN_BEST_PRACTICE_5_3_1]: {
    label: 'Questionnaire v5.3.1',
    version: 'v5.3.1',
    questionnaireVersion: QuestionnaireVersions.V_5_3_1,
    questionnaireTitle: 'TPN Best Practices Questionnaire v5.3.1',
    sortOrder: 3,
  },
};

export const WorkflowLabels = [
  TPN_BEST_PRACTICE_5_1,
  TPN_BEST_PRACTICE_5_2,
  TPN_BEST_PRACTICE_5_3,
  TPN_BEST_PRACTICE_5_3_1,
];

export const workflowLabels = (wf: string) => {
  return WORKFLOW_MAP[wf as keyof WorkflowMap]?.label || wf;
};

export const workflowVersion = (workflowType?: string) => {
  return WORKFLOW_MAP[workflowType as keyof WorkflowMap]?.version || '';
};

export const isWorkflow531OrHigher = (workflowType: string) => {
  return !(workflowType === TPN_BEST_PRACTICE_5_1 || workflowType === TPN_BEST_PRACTICE_5_2 || workflowType === TPN_BEST_PRACTICE_5_3);
};

export const ReadOnlyBaselineWorkflowStatusArray: WorkflowStatus[] = [
  WORKFLOW_STATUSES.SUBMITTED_FOR_ASSESSMENT,
  WORKFLOW_STATUSES.ASSIGNED,
  WORKFLOW_STATUSES.ASSESSING,
  WORKFLOW_STATUSES.SUBMITTED,
  WORKFLOW_STATUSES.REJECTED,
  WORKFLOW_STATUSES.REMEDIATION,
  WORKFLOW_STATUSES.FINAL_COMPLETE,
];

export const CanNotSetBackToIncompleteWorkflowStatusArray: WorkflowStatus[] = [
  WORKFLOW_STATUSES.ASSESSING,
  WORKFLOW_STATUSES.SUBMITTED,
  WORKFLOW_STATUSES.REJECTED,
  WORKFLOW_STATUSES.REMEDIATION,
  WORKFLOW_STATUSES.FINAL_COMPLETE,
];